import { DataService } from 'api/DataService'
import { aggregatedScope } from 'lib/merchant-group'

import {
  Notification,
  NotificationsListResponse,
  MarkAllReadResponse,
  NotificationType,
  NotificationPreferences
} from './types'

export const notificationService = {
  /**
   * List notifications for the current user
   */
  list: async (params?: {
    read?: boolean
    notification_type?: NotificationType
    page?: number
    per_page?: number
  }): Promise<NotificationsListResponse> => {
    const queryParams: string[] = []

    if (params?.read !== undefined) {
      queryParams.push(`read=${params.read}`)
    }
    if (params?.notification_type) {
      queryParams.push(`notification_type=${params.notification_type}`)
    }
    if (params?.page) {
      queryParams.push(`page=${params.page}`)
    }
    if (params?.per_page) {
      queryParams.push(`per=${params.per_page}`)
    }

    const url =
      queryParams.length > 0
        ? `/v1/notifications?${queryParams.join('&')}`
        : '/v1/notifications'

    const response = await DataService({
      url,
      type: 'GET',
      // SC-10999: no grupo, as notificações de todas as empresas
      scope: aggregatedScope()
    })

    if (response.status === 200) {
      return {
        notifications: response.data.records || [],
        meta: response.data.meta || {
          current_page: 1,
          next_page: null,
          prev_page: null,
          total_pages: 0,
          total_count: 0,
          unread_count: 0
        }
      }
    }

    return {
      notifications: [],
      meta: {
        current_page: 1,
        next_page: null,
        prev_page: null,
        total_pages: 0,
        total_count: 0,
        unread_count: 0
      }
    }
  },

  /**
   * Get a single notification by UUID
   */
  get: async (uuid: string): Promise<Notification | null> => {
    const response = await DataService({
      url: `/v1/notifications/${uuid}`,
      type: 'GET'
    })

    if (response.status === 200) {
      return response.data.record || null
    }

    return null
  },

  /**
   * Mark a notification as read
   */
  markAsRead: async (uuid: string): Promise<Notification | null> => {
    const response = await DataService({
      url: `/v1/notifications/${uuid}/mark_read`,
      type: 'POST',
      // SC-10999: no grupo, as notificações de todas as empresas
      scope: aggregatedScope()
    })

    if (response.status === 200) {
      return response.data.record || null
    }

    return null
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async (): Promise<MarkAllReadResponse> => {
    const response = await DataService({
      url: '/v1/notifications/mark_all_read',
      type: 'POST',
      // SC-10999: no grupo, as notificações de todas as empresas
      scope: aggregatedScope()
    })

    if (response.status === 200) {
      return {
        records_updated: response.data.records_updated || 0
      }
    }

    return { records_updated: 0 }
  },

  /**
   * Get unread notifications count
   */
  getUnreadCount: async (): Promise<number> => {
    const response = await DataService({
      url: '/v1/notifications/unread_count',
      type: 'GET',
      // SC-10999: no grupo, as notificações de todas as empresas
      scope: aggregatedScope()
    })

    if (response.status === 200) {
      return response.data.unread_count || 0
    }

    return 0
  },

  /**
   * Get user notification preferences
   */
  getPreferences: async (): Promise<NotificationPreferences> => {
    const response = await DataService({
      url: '/v1/notifications/preferences',
      type: 'GET'
    })

    if (response.status === 200) {
      return response.data.preferences || {}
    }

    return {}
  },

  /**
   * Update user notification preferences
   */
  updatePreferences: async (
    preferences: Partial<NotificationPreferences>
  ): Promise<NotificationPreferences> => {
    const response = await DataService({
      url: '/v1/notifications/preferences',
      type: 'PUT',
      data: { preferences }
    })

    if (response.status === 200) {
      return response.data.preferences || {}
    }

    return {}
  }
}
